import { Place } from '@polaris-customerweb/sharedcomponents.map';
import { GeoCoordinate, MailingAddress, findClosestGeoCoordinate } from '@polaris-customerweb/sharedcomponents.shared';
import { PlannedRideTrack, PlannedRideWaypoint } from '@polaris-customerweb/sharedcomponents.user-content';
import { first, get, compact, upperFirst, intersection } from 'lodash-es';

class SearchResult {
    constructor() {
        this.id = '';
        this.type = '';
        this.name = '';
        this.description = '';
        this.icon = '';
        this.iconColor = '';
        this.imageUrl = '';
        this.thumbImageUrl = '';
        this.point = new GeoCoordinate();
        this.isCoordinate = false;
        this.poiCategories = [];
    }
    static { this.itemTypes = {
        adhocWaypoint: 'AdHocWaypoint',
        adventure: 'Adventure',
        collections: 'Collection',
        dealer: 'Dealer',
        geofences: 'Geofence',
        location: 'Location',
        plannedRide: 'PlannedRide',
        poi: 'PointOfInterest',
        privateRidePark: 'PrivateRidePark',
        publicWaypoint: 'PublicWaypoint',
        rideArea: 'TrailSystem',
        sponsor: 'PointOfInterest',
        trackedRide: 'TrackedRide',
        waypoint: 'Waypoint',
    }; }
    static { this.poiIcons = {
        'poi-bank': '/assets/icons/map-icons/shared/poi-bank.svg',
        'poi-bar': '/assets/icons/map-icons/shared/poi-bar.svg',
        'poi-business': '/assets/icons/map-icons/shared/poi-business.svg',
        'poi-campground': '/assets/icons/map-icons/shared/poi-campground.svg',
        'poi-food': '/assets/icons/map-icons/shared/poi-food.svg',
        'poi-food-premium': '/assets/icons/map-icons/shared/poi-food-premium.svg',
        'poi-fuel': '/assets/icons/map-icons/shared/poi-fuel.svg',
        'poi-fuel-premium': '/assets/icons/map-icons/shared/poi-fuel-premium.svg',
        'poi-lodging': '/assets/icons/map-icons/shared/poi-lodging.svg',
        'poi-lodging-premium': '/assets/icons/map-icons/shared/poi-lodging-premium.svg',
        'poi-medical': '/assets/icons/map-icons/shared/poi-medical.svg',
        'poi-other': '/assets/icons/map-icons/shared/poi-other.svg',
        'poi-park': '/assets/icons/map-icons/shared/poi-park.svg',
        'poi-parking': '/assets/icons/map-icons/shared/poi-parking.svg',
        'poi-shopping': '/assets/icons/map-icons/shared/poi-shopping.svg',
        'poi-waypoint-bridge': '/assets/icons/map-icons/shared/poi-waypoint-bridge.svg',
        'poi-waypoint-camping': '/assets/icons/map-icons/shared/poi-waypoint-camping.svg',
        'poi-waypoint-drinking-water': '/assets/icons/map-icons/shared/poi-waypoint-drinking-water.svg',
        'poi-waypoint-historic-site': '/assets/icons/map-icons/shared/poi-waypoint-historic-site.svg',
        'poi-waypoint-junction': '/assets/icons/map-icons/shared/poi-waypoint-junction.svg',
        'poi-waypoint-mud': '/assets/icons/map-icons/shared/poi-waypoint-mud.svg',
        'poi-waypoint-parking': '/assets/icons/map-icons/shared/poi-waypoint-parking.svg',
        'poi-waypoint-picnic-area': '/assets/icons/map-icons/shared/poi-waypoint-picnic-area.svg',
        'poi-waypoint-restrooms': '/assets/icons/map-icons/shared/poi-waypoint-restrooms.svg',
        'poi-waypoint-scenic': '/assets/icons/map-icons/shared/poi-waypoint-scenic.svg',
        'poi-waypoint-stream-crossing': '/assets/icons/map-icons/shared/poi-waypoint-stream-crossing.svg',
        'poi-waypoint-structure': '/assets/icons/map-icons/shared/poi-waypoint-structure.svg',
        'poi-waypoint-trailhead': '/assets/icons/map-icons/shared/poi-waypoint-trailhead.svg',
        'poi-waypoint-wash': '/assets/icons/map-icons/shared/poi-waypoint-wash.svg',
    }; }
    static { this.rideAreaOrvIcon = '/assets/icons/map-icons/shared/poi-ridearea-orv.svg'; }
    static { this.rideAreaSnowIcon = '/assets/icons/map-icons/shared/poi-ridearea-snow.svg'; }
    static { this.waypointIcon = '/assets/icons/map-icons/shared/waypoint.svg'; }
    static { this.geofenceIcon = '/assets/icons/map-icons/shared/geofence.svg'; }
    static { this.publicWaypointIcon = '/assets/icons/map-icons/shared/poi-waypoint-marksolid.svg'; }
    static { this.plannedRideIcons = {
        ind: '/assets/icons/map-icons/shared/planned-ride.svg',
        orv: '/assets/icons/map-icons/shared/planned-ride.svg',
        slg: '/assets/icons/map-icons/shared/planned-ride.svg',
    }; }
    static { this.trackedRideIcon = '/assets/icons/map-icons/shared/tracked-ride.svg'; }
    static { this.dealerIcons = {
        ind: '/assets/icons/map-icons/indian/poi-dealer.svg',
        orv: '/assets/icons/map-icons/offroad/poi-dealer.svg',
        slg: '/assets/icons/map-icons/slingshot/poi-dealer.svg',
    }; }
    static { this.adventureIcons = {
        ind: '/assets/icons/map-icons/indian/adventures.svg',
        orv: '/assets/icons/map-icons/offroad/adventures.svg',
        slg: '/assets/icons/map-icons/slingshot/adventures.svg',
    }; }
    static { this.geographyIcon = '../assets/icons/bullseye.svg'; }
    static { this.collectionIcon = '../assets/icons/map-icons/shared/collection.svg'; }
    get mailingAddress() {
        const address = new MailingAddress({
            city: this.place ?? '',
            country: this.country ?? '',
            postalCode: this.postalCode ?? '',
            state: this.region ?? '',
            street: this.address ?? '',
        });
        return address.isEmpty() ? undefined : address;
    }
    /* Used with the location filter currently in community items. */
    static fromLocationFilter(name, description, point) {
        const item = new SearchResult();
        item.name = name;
        item.description = description;
        item.type = SearchResult.itemTypes.location;
        item.point = point;
        return item;
    }
    static fromGeofence(geofence, mapCenter) {
        const item = new SearchResult();
        item.id = geofence.id;
        item.color = geofence.color;
        item.name = geofence.name;
        item.snapshots = [...geofence.snapshots];
        item.imageUrl = SearchResult.getUrl([...geofence.snapshots], false);
        item.thumbImageUrl = SearchResult.getUrl([...geofence.snapshots], true);
        item.type = SearchResult.itemTypes.geofences;
        item.icon = SearchResult.geofenceIcon;
        const closestPoint = findClosestGeoCoordinate(geofence.polygon, mapCenter);
        item.point = closestPoint;
        item.distanceFromCenter = closestPoint.distanceFrom(mapCenter);
        item.searchType = 'area';
        item.totalArea = geofence.area;
        return item;
    }
    static fromTrackedRide(trackedRide, mapCenter) {
        const item = new SearchResult();
        item.id = trackedRide.id;
        item.color = trackedRide.color;
        item.name = trackedRide.name;
        item.description = trackedRide.description;
        item.snapshots = [...trackedRide.snapshots];
        item.imageUrl = SearchResult.getUrl(trackedRide.snapshots, false);
        item.thumbImageUrl = SearchResult.getUrl(trackedRide.snapshots, true);
        item.type = SearchResult.itemTypes.trackedRide;
        item.icon = SearchResult.trackedRideIcon;
        const closestPoint = findClosestGeoCoordinate(trackedRide.polyLine, mapCenter);
        item.point = closestPoint;
        item.distanceFromCenter = closestPoint.distanceFrom(mapCenter);
        item.searchType = 'trackedRide';
        item.totalLengthMeters = trackedRide.totalLengthMeters;
        item.totalDurationSeconds = trackedRide.totalDurationSeconds;
        return item;
    }
    static fromRideArea(rideArea, mapCenter) {
        const item = new SearchResult();
        item.id = rideArea.id;
        if (rideArea.isOrv) {
            item.icon = SearchResult.rideAreaOrvIcon;
        }
        else {
            item.icon = SearchResult.rideAreaSnowIcon;
        }
        item.name = rideArea.name;
        item.description = rideArea.mainAddress.toCityStateString();
        item.type = SearchResult.itemTypes.rideArea;
        item.point = rideArea.geoCoordinate;
        item.distanceFromCenter = item.point.distanceFrom(mapCenter);
        item.searchType = 'trailSystem';
        return item;
    }
    static fromWaypoint(waypoint, mapCenter) {
        const item = new SearchResult();
        item.id = waypoint.id;
        item.color = waypoint.color;
        item.name = waypoint.name;
        item.snapshots = [...waypoint.snapshots];
        item.imageUrl = SearchResult.getUrl(waypoint.snapshots, false);
        item.thumbImageUrl = SearchResult.getUrl(waypoint.snapshots, true);
        item.point = waypoint.geoCoordinate;
        item.type = SearchResult.itemTypes.waypoint;
        item.distanceFromCenter = mapCenter ? item.point.distanceFrom(mapCenter) : undefined;
        item.tags = waypoint.tags;
        item.icon = SearchResult.waypointIcon;
        item.searchType = 'waypoint';
        return item;
    }
    static fromPublicWaypoint(waypoint, mapCenter) {
        const item = SearchResult.fromWaypoint(waypoint, mapCenter);
        item.type = SearchResult.itemTypes.publicWaypoint;
        let tag = first(waypoint.tags) || '';
        tag = tag.replace('_', '-');
        item.icon = (get(this, ['poiIcons', `poi-waypoint-${tag}`]) ?? SearchResult.publicWaypointIcon);
        item.tags = waypoint.tags;
        item.searchType = 'publicWaypoint';
        return item;
    }
    static fromPoi(poi, mapCenter) {
        const item = new SearchResult();
        item.id = poi.id;
        item.name = poi.name;
        item.description = poi.address.toString();
        item.point = poi.geoCoordinate;
        item.type = SearchResult.itemTypes.poi;
        item.distanceFromCenter = item.point.distanceFrom(mapCenter);
        item.icon = (get(SearchResult.poiIcons, poi.icon) ?? get(SearchResult.poiIcons, 'poi-other'));
        item.searchType = 'poi';
        return item;
    }
    static toPoi(item) {
        return new Place({
            geometry: {
                coordinates: [item.point.lng, item.point.lat],
                type: 'Point',
            },
            properties: {
                // TODO: add toPlainObject method to Search
                // eslint-disable-next-line @typescript-eslint/no-misused-spread
                ...item,
                address: new MailingAddress(),
                id: item.id || '',
                phone: '',
                placeType: this.getPoiCategory(item.poiCategories) ?? 'Other',
                website: '',
            },
            type: 'Feature',
        });
    }
    static fromSponsor(sponsor, mapCenter) {
        const item = new SearchResult();
        item.id = sponsor.id;
        item.name = sponsor.name;
        item.description = sponsor.address.toString();
        item.point = sponsor.geoCoordinate;
        item.type = SearchResult.itemTypes.sponsor;
        item.distanceFromCenter = item.point.distanceFrom(mapCenter);
        item.icon = (get(SearchResult.poiIcons, sponsor.icon) ?? get(SearchResult.poiIcons, 'poi-other'));
        item.searchType = 'sponsor';
        return item;
    }
    static fromDealer(dealer, mapCenter, mapType) {
        const item = new SearchResult();
        item.id = dealer.id;
        item.name = dealer.name;
        item.description = dealer.address.toString();
        item.point = dealer.geoCoordinate;
        item.type = SearchResult.itemTypes.dealer;
        item.distanceFromCenter = item.point.distanceFrom(mapCenter);
        item.icon = get(SearchResult.dealerIcons, mapType);
        item.searchType = 'dealer';
        return item;
    }
    static fromAdventure(adventure, mapCenter, mapType) {
        const item = new SearchResult();
        item.description = adventure.name;
        item.distanceFromCenter = adventure.geoCoordinate.distanceFrom(mapCenter);
        item.icon = adventure.dealerId
            ? get(SearchResult.dealerIcons, mapType)
            : get(SearchResult.adventureIcons, mapType);
        item.id = adventure.id;
        item.name = adventure.name;
        item.point = adventure.geoCoordinate;
        item.searchType = 'outfitter';
        item.type = SearchResult.itemTypes.adventure;
        return item;
    }
    static fromPlannedRide(plannedRide, mapCenter, mapType) {
        const item = new SearchResult();
        item.id = plannedRide.id;
        item.color = plannedRide.color;
        item.name = plannedRide.name;
        item.description = plannedRide.description;
        item.type = SearchResult.itemTypes.plannedRide;
        item.icon = get(SearchResult.plannedRideIcons, mapType);
        item.snapshots = [...plannedRide.snapshots];
        item.imageUrl = SearchResult.getUrl([...plannedRide.snapshots], false);
        item.thumbImageUrl = SearchResult.getUrl([...plannedRide.snapshots], true);
        const closestPoint = findClosestGeoCoordinate(plannedRide.geoCoordinates, mapCenter);
        item.point = closestPoint;
        item.distanceFromCenter = closestPoint.distanceFrom(mapCenter);
        item.searchType = 'plannedRide';
        item.totalLengthMeters = plannedRide.features.find((x) => x instanceof PlannedRideTrack).totalLengthInMeters;
        return item;
    }
    static fromSearchFeatureContract(searchResultDto, searchType, mapType = 'orv') {
        const item = this.buildSearchResult(searchResultDto, searchType, mapType);
        if (searchResultDto.geometry?.coordinates) {
            item.point = GeoCoordinate.fromCoordinateArray(searchResultDto.geometry.coordinates);
        }
        return item;
    }
    static fromSuggestFeatureContract(sessionId, suggestResultDto, searchType, mapType = 'orv') {
        const item = this.buildSearchResult(suggestResultDto, searchType, mapType);
        item.sessionId = sessionId;
        item.suggestId = suggestResultDto.properties?.suggest?.id;
        if (suggestResultDto.geometry?.coordinates) {
            item.point = GeoCoordinate.fromCoordinateArray(suggestResultDto.geometry.coordinates);
        }
        return item;
    }
    static toAdHocWaypoint(searchResult) {
        const adhocWaypoint = PlannedRideWaypoint.fromCoordinateArray([
            searchResult.point.longitude,
            searchResult.point.latitude,
        ]);
        adhocWaypoint.plannedRideId = adhocWaypoint.id;
        adhocWaypoint.originalId = searchResult.id || '';
        adhocWaypoint.originalType = searchResult.type === 'Location' ? 'location' : 'adhocWaypoint';
        adhocWaypoint.name =
            adhocWaypoint.originalType === 'adhocWaypoint' ? adhocWaypoint.geoCoordinate.toShortString(6) : searchResult.name;
        adhocWaypoint.description = searchResult.description;
        adhocWaypoint.id = searchResult.id || '';
        adhocWaypoint.isAdhocFromSearchResult = true;
        return adhocWaypoint;
    }
    static toMapItemType(searchType) {
        switch (searchType) {
            case 'geography':
            case 'poi':
            case 'poI_Foursquare':
                return 'poi';
            case 'area':
                return 'geofence';
            case 'plannedRide':
                return 'plannedRide';
            case 'trackedRide':
                return 'trackedRide';
            case 'waypoint':
                return 'waypoint';
            case 'sponsor':
                return 'premiumPoi';
            case 'trailSystem':
                return 'rideArea';
            case 'collection':
                return 'collection';
            case 'dealer':
                return 'dealer';
            case 'publicWaypoint':
                return 'publicWaypoint';
            case 'outfitter':
                return 'adventure';
            default:
                return 'emptyArea';
        }
    }
    static getUrl(snapshots, isThumbnail) {
        let prevSize = 0;
        return snapshots.reduce((url, snapshot) => {
            const numbers = snapshot.size?.match(/[0-9]+/g);
            if (numbers && numbers.length === 2) {
                const [xSize, ySize] = numbers;
                const newSize = Number(xSize) * Number(ySize);
                const isNewLarger = newSize > prevSize;
                if (!prevSize || (isThumbnail && !isNewLarger) || (!isThumbnail && isNewLarger)) {
                    prevSize = newSize;
                    return snapshot.url;
                }
            }
            return url;
        }, '');
    }
    static getOriginalType(searchType, isLocation) {
        switch (searchType) {
            case 'adhocWaypoint':
            case 'geography':
                return isLocation ? 'location' : 'adhocWaypoint';
            case 'outfitter':
                return 'adventure';
            case 'dealer':
                return 'dealer';
            case 'poi':
            case 'poI_Foursquare':
            case 'sponsor':
                return 'poi';
            case 'publicWaypoint':
            case 'waypoint':
                return 'waypoint';
            default:
                return 'generated';
        }
    }
    static mapSearchType(searchType) {
        switch (searchType) {
            case 'adhocWaypoint':
                return SearchResult.itemTypes.adhocWaypoint;
            case 'geography':
                return SearchResult.itemTypes.location;
            // mapbox poi that would render as location
            case 'poi':
                return SearchResult.itemTypes.location;
            case 'poI_Foursquare':
                return SearchResult.itemTypes.poi;
            case 'area':
                return SearchResult.itemTypes.geofences;
            case 'plannedRide':
                return SearchResult.itemTypes.plannedRide;
            case 'trackedRide':
                return SearchResult.itemTypes.trackedRide;
            case 'waypoint':
                return SearchResult.itemTypes.waypoint;
            case 'sponsor':
                return SearchResult.itemTypes.sponsor;
            case 'trailSystem':
                return SearchResult.itemTypes.rideArea;
            case 'collection':
                return SearchResult.itemTypes.collections;
            case 'dealer':
                return SearchResult.itemTypes.dealer;
            case 'publicWaypoint':
                return SearchResult.itemTypes.publicWaypoint;
            case 'outfitter':
                return SearchResult.itemTypes.adventure;
            default:
                return '';
        }
    }
    // TODO: create search and suggest contracts for all possible result types and get rid of all the lodash get calls here.
    static buildSearchResult(searchResultDto, searchType, mapType = 'orv') {
        const item = new SearchResult();
        const properties = searchResultDto.properties;
        item.id = properties.id;
        item.searchType = searchType;
        item.correlated = searchResultDto.properties?.correlated;
        item.foursquareId = searchResultDto.properties?.foursquareId;
        if (searchType === 'adhocWaypoint') {
            item.type = this.mapSearchType(searchType);
            item.name = properties.name || '';
        }
        else if (searchType === 'geography' || searchType === 'poi') {
            item.name = properties.name || '';
            item.type = this.mapSearchType(searchType);
            item.address = properties.address?.street;
            item.place = properties.address?.place;
            item.region = properties.address?.region;
            item.country = properties.address?.country;
            item.postalCode = properties.address?.postalCode;
            item.description =
                searchType === 'geography'
                    ? compact([item.place, item.region]).join(', ')
                    : compact([
                        properties.address?.address,
                        properties.address?.place,
                        properties.address?.region,
                        properties.address?.regionCode ?? properties.address?.region,
                    ]).join(', ');
            // item.poiCategories = searchResultDto.properties?.poiCategory || [];
        }
        else if (searchType === 'poI_Foursquare' || searchType === 'sponsor') {
            item.name = properties.name || '';
            item.type = this.mapSearchType(searchType);
            item.address = properties.address?.street;
            item.place = properties.address?.city;
            item.region = properties.address?.region || properties.address?.state;
            item.country = properties.address?.country;
            item.postalCode = properties.address?.postalCode;
            item.description = new MailingAddress(properties.address).toString();
        }
        else if (searchType === 'waypoint' ||
            searchType === 'trackedRide' ||
            searchType === 'plannedRide' ||
            searchType === 'area') {
            item.color =
                get(properties, 'color') || get(properties, 'tintForTagIcon');
            item.name = properties.name || '';
            item.type = this.mapSearchType(searchType);
            item.snapshots = get(properties, 'snapshots');
            item.tags = get(properties, 'tags');
            let plannedRideProps;
            switch (searchType) {
                case 'area':
                    item.color = get(properties, ['featureCollection', 'features', 0, 'properties', 'color']);
                    item.totalArea = get(properties, ['featureCollection', 'features', 0, 'properties', 'area']);
                    break;
                case 'plannedRide':
                    plannedRideProps = properties.featureCollection?.features?.find((feature) => feature.properties?.featureType === 'PlannedTrack')?.properties;
                    item.color = get(plannedRideProps, 'color');
                    item.totalLengthMeters = get(plannedRideProps, 'totalLengthInMeters');
                    break;
                default:
                    item.totalLengthMeters = properties.totalLengthMeters;
            }
            item.totalDurationSeconds = properties.totalDurationSeconds;
        }
        else if (searchType === 'collection') {
            item.name = properties.name || '';
            item.type = this.mapSearchType(searchType);
            item.snapshots = properties.snapshots;
            item.linkedItems = properties.linkedItems;
            const length = properties.linkedItems?.length;
            item.description = length === 1 ? $localize `1 item` : `${length} ${$localize `:as in '3 items':items`}`;
        }
        else if (searchType === 'dealer') {
            item.id ||= properties.dealerId || '';
            item.name = properties.name || properties.dealerName || '';
            item.type = this.mapSearchType(searchType);
            item.address = properties.address?.street;
            item.place = properties.address?.city;
            item.region = properties.address?.state;
            item.postalCode = properties.address?.postalCode;
            item.country = properties.address?.country;
            item.description = new MailingAddress(properties.address).toString();
        }
        else if (searchType === 'outfitter') {
            item.id ||= properties.outfitterId?.toString() || '';
            item.name = properties.name || properties.outfitterName || '';
            item.type = this.mapSearchType(searchType);
            item.address = properties.addressLine1;
            item.place = properties.city;
            item.region = properties.stateProvince;
            item.postalCode = properties.zip;
            item.country = properties.country;
            item.description = new MailingAddress({
                city: properties.city,
                state: properties.stateProvince,
                street: properties.addressLine1,
            }).toString();
        }
        else if (searchType === 'trailSystem') {
            item.id ||= properties.uuid || '';
            item.name = properties.name || '';
            item.type = this.mapSearchType(searchType);
            item.description = new MailingAddress(properties.mainAddress).toCityStateString();
        }
        else if (searchType === 'publicWaypoint') {
            item.name = properties.name || '';
            item.type = this.mapSearchType(searchType);
            item.snapshots = properties.snapshots;
            item.description = properties.address ? new MailingAddress(properties.address).toCityStateString() : '';
            item.tags = properties.tags;
        }
        if (item.searchType === 'geography') {
            // Mapbox address results do not have IDs so use full address
            item.id = `${item.name} ${item.description}`;
        }
        let icon = SearchResult.poiIcons['poi-other'];
        let sponsorType;
        switch (item.searchType) {
            case 'geography':
            case 'adhocWaypoint':
                icon = SearchResult.geographyIcon;
                break;
            case 'poi':
            case 'poI_Foursquare':
                icon = SearchResult.poiIcons[this.mapPoiIcon(searchResultDto.properties?.poiCategory || [])];
                break;
            case 'sponsor':
                sponsorType = searchResultDto.properties?.placeType || '';
                icon =
                    SearchResult.poiIcons[this.mapPoiIcon(typeof sponsorType === 'string' ? [sponsorType] : sponsorType, true)];
                break;
            case 'waypoint':
                icon = SearchResult.waypointIcon;
                break;
            case 'trackedRide':
                icon = SearchResult.trackedRideIcon;
                break;
            case 'trailSystem':
                icon = this.mapTrailSystemIcon(properties.vehicleClasses || []) || icon;
                break;
            case 'plannedRide':
                icon = SearchResult.plannedRideIcons[mapType];
                break;
            case 'area':
                icon = SearchResult.geofenceIcon;
                break;
            case 'collection':
                icon = SearchResult.collectionIcon;
                break;
            case 'dealer':
                icon = SearchResult.dealerIcons[mapType];
                break;
            case 'outfitter':
                icon = SearchResult.adventureIcons[mapType];
                break;
            case 'publicWaypoint':
                icon = SearchResult.publicWaypointIcon;
                break;
            default:
            // do nothing
        }
        item.icon = icon;
        return item;
    }
    static getPoiCategory(categories, isPremium = false) {
        const premiumPoi = ['food', 'fuel', 'gas', 'lodging'];
        const regularPoi = [
            // 'bank',
            // 'bar',
            // 'business',
            // 'campground',
            'medical',
            'health',
            // 'park',
            // 'parking',
            // 'shopping',
            ...premiumPoi,
        ];
        const keywords = isPremium ? premiumPoi : regularPoi;
        for (const category of categories) {
            let match = keywords.find((keyword) => category.toLowerCase().includes(keyword));
            if (match) {
                if (match === 'gas') {
                    match = 'Fuel';
                }
                if (match === 'health') {
                    match = 'Medical';
                }
                return upperFirst(match);
            }
        }
        return null;
    }
    static mapPoiIcon(categories, isPremium = false) {
        const category = this.getPoiCategory(categories, isPremium)?.toLocaleLowerCase() ?? 'other';
        return (isPremium ? `poi-${category}-premium` : `poi-${category}`);
    }
    static mapTrailSystemIcon(vehicleClasses) {
        if (intersection(vehicleClasses, ['SNO']).length > 0) {
            return SearchResult.rideAreaSnowIcon;
        }
        if (intersection(vehicleClasses, ['ATV', 'ROV', 'ROV50', 'ORV', 'OHV', 'UTV', 'OHM', 'HLV']).length > 0) {
            return SearchResult.rideAreaOrvIcon;
        }
        return '';
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { SearchResult };
